import React, { useState, useMemo, useRef, useEffect } from 'react';
import zipcelx from 'zipcelx';
import { cloneDeep } from 'lodash';
import Highcharts, { Options as HighchartsOptions } from 'highcharts';
import '~/shared/utils/highchartsAllowList';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import Humanize from 'humanize-plus';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCExportData from 'highcharts/modules/export-data';
import HCNoData from 'highcharts/modules/no-data-to-display';
import styled from 'styled-components';
import CustomEvents from 'highcharts-custom-events';
import wordCloud from 'highcharts/modules/wordcloud.js';
import moment from 'moment';
import { highchartsTooltipCfg } from '~/shared/config/highchartsCfg';
import { hex2rgba } from '../../../utils/colors/hex2rgba';
import { formatTooltipHtml } from '~/shared/utils';
import { buildChartExportFileName } from '~/shared/utils/buildChartExportFileName';

HCExporting(Highcharts);
HCExportData(Highcharts);
HCNoData(Highcharts);
HCOfflineExporting(Highcharts);
CustomEvents(Highcharts as any);
wordCloud(Highcharts);
HCAccessibility(Highcharts);

(function (H) {
  H.Chart.prototype.downloadXLS = function () {
    const div = document.createElement('div');
    let xlsxRows = [];
    div.style.display = 'none';
    document.body.appendChild(div);
    const rows = this.getDataRows(true);
    xlsxRows = H.map(rows.slice(1), function (row: any) {
      return H.map(row, function (column: any) {
        let tempColumn = column;
        if (typeof column === 'string' && column?.includes('00:00:00')) {
          tempColumn = moment(column).format('MM/DD/YYYY');
        }
        return {
          type: typeof column === 'number' ? 'number' : 'string',
          value: tempColumn,
        };
      });
    });

    const name = this.options.exporting!.filename;
    zipcelx({
      filename: name as unknown as string,
      sheet: {
        data: xlsxRows,
      },
    });
  };
})(Highcharts);

const EMPTY_OPTIONS: HighchartsOptions = { series: [] };

export interface IWordCloudChart {
  axisXLabels: string[];
  axisYLabels: string[];
  values: { XLabel: string; YLabel: string; Value: number }[];
  matrix: number[][];
  data?: any;
}

const Root = styled.div<{ $gridheight: number }>`
  height: 100%;
  width: 100%;
  position: relative;
  margin-top: 10px;
`;

const HighchartContainer = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  border: solid 1px var(--border-chart);
  background: var(--background-chart);
  padding: 10px;
  .highcharts-button-symbol {
    width: 3.5px !important;
    fill: #4c5e72 !important;
    stroke: transparent !important;
  }
  .highcharts-axis {
    display: none;
  }
  .highcharts-button-box {
    x: 2;
    y: 1;
    rx: 16;
    ry: 16;
    &:hover {
      fill: #d4e5ff !important;
    }
  }
  .highcharts-button-symbol {
    width: 3.5px !important;
    fill: #4c5e72 !important;
    stroke: transparent !important;
    pointer-events: none;
  }
`;

Highcharts.SVGRenderer.prototype.symbols.ellipsisvertical = function () {
  const path = [
    'M18.1538 16C18.1538 17.1451 17.222 18.0769 16.0769 18.0769C14.9318 18.0769 14 17.1451 14 16C14 14.8549 14.9318 13.9231 16.0769 13.9231C17.222 13.9231 18.1538 14.8549 18.1538 16Z M18.1538 22.9231C18.1538 24.0682 17.222 25 16.0769 25C14.9318 25 14 24.0682 14 22.9231C14 21.778 14.9318 20.8462 16.0769 20.8462C17.222 20.8462 18.1538 21.778 18.1538 22.9231Z M18.1538 9.07692C18.1538 10.222 17.222 11.1538 16.0769 11.1538C14.9318 11.1538 14 10.222 14 9.07692C14 7.93185 14.9318 7 16.0769 7C17.222 7 18.1538 7.93185 18.1538 9.07692Z',
  ];
  return path;
};

export default function WordCloudChart({
  data,
  size,
  onClick,
  prism,
  filterValues,
  colorScheme,
  chartTitle,
  chartSubtitle,
  chartYtitle,
  tooltipCfg,
  gridHeight,
  gridIndex,
  gridPrism,
  chartYmax,
  chartYmin,
}: any) {
  const chartRef = useRef<any>(null);

  const textBoldShadow = '0 0 .01px black, 0 0 .01px black, 0 0 .01px black';

  const [labelClicked, setLabelClicked] = useState(false);

  const exportFileName = buildChartExportFileName(chartTitle, filterValues, prism);

  useEffect(() => {
    const onScroll = () => {
      const elements = document.getElementsByClassName('xLabel');

      for (let i = 0; i < elements.length; i++) {
        (elements[i] as unknown as any).style.display = 'none';
      }
    };
    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    chartRef.current.reflow();
  }, [chartRef, size]);

  const chartOptions = useMemo(() => {
    const sortedData: IWordCloudChart = cloneDeep({
      axisYLabels: data?.row_nodes?.map((r: any) => r.name),
      axisXLabels: data?.col_nodes?.map((c: any) => c.name),
      matrix: data?.data,
      data: data,
      values: [],
    });

    let maxValue = 0;

    const categoriesData: string[] = [];
    const seriesData: any[] = [];

    for (let j = 0; j < sortedData.axisXLabels?.length; ++j) {
      for (let i = 0; i < sortedData.axisYLabels?.length; ++i) {
        if (j === 0) categoriesData.push(sortedData.axisYLabels[i]);
        const objData = data.data[i][sortedData.axisXLabels?.length - j - 1];
        seriesData.push({ name: sortedData.axisYLabels[i], weight: objData });
      }
    }

    data?.data?.forEach((d: any) => {
      if (d[1] + d[0] > maxValue) maxValue = d[1] + d[0];
    });

    if (!seriesData) return;

    return {
      chart: {
        className: prism.replace(' ', '') + gridIndex,
        type: 'wordcloud',
        marginLeft: -50,
        marginRight: data && data?.length < 3 ? 35 : undefined,
        marginTop: 30,
        height: (gridHeight / 4) * 446 + (gridHeight / 4 - 1) * 38,
        animation: false,
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
          left: '0px',
        },
      },
      exporting: {
        filename: exportFileName,
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof Highcharts.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        enabled: true,
        buttons: {
          contextButton: {
            x: 8,
            y: 2,
            symbol: 'ellipsisvertical',
            menuClassName: 'highcharts-indicator-menu',
            menuItems: [
              'viewFullscreen',
              'downloadPNG',
              'downloadSVG',
              'downloadPDF',
              'separator',
              'downloadXLS',
              'downloadCSV',
            ],
          },
        },
        fallbackToExportServer: false,
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 12,
        y: 24,
        style: {
          fontSize: '18px',
          color: 'var(--color-chart-title)',
          fontWeight: 500,
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      subtitle: {
        text: chartSubtitle,
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: categoriesData,
        gridZIndex: -1,
        zIndex: -1,
        labels: {
          formatter: function (): string {
            const obj = this as unknown as any;
            return `<div data-id='${`${prism.replace(' ', '')}${gridIndex}`}' id='${data.row_nodes[obj.pos].id}#${
              data.title
            }' data-name='${obj.value}' style='text-shadow:none' class="clickableLabel">${
              obj.value.length > 20 ? `${obj.value.substring(0, 17)}...` : obj.value
            }</div>`;
          },
          events: {
            mouseover: function () {
              if (labelClicked) return;
              const chartBox = document.querySelector(`.${prism.replace(' ', '')}${gridIndex}`);
              const chartRect = chartBox && chartBox.getBoundingClientRect();
              const obj = this as unknown as any;
              const value = obj.value.split("'")[3];
              const name = obj.value.split("'")[5];
              const objBox = document.querySelector(`[id="${value}"`);
              const objRect = objBox && objBox.getBoundingClientRect();
              const objOrg = data.row_nodes.filter((l: any) => l.name === value);
              const description = objOrg[0]?.description;
              const chart = chartRef.current;
              if (chart.xAxisTooltip !== undefined) chart.xAxisTooltip = undefined;
              const tooltipHtml = formatTooltipHtml({
                title: name,
                description,
                tooltipCfg,
                letterSpacingAdjustment: -0.75,
              });
              chart.xAxisTooltip = chart.renderer
                .label(tooltipHtml, 0, 0, 'rectangle', undefined, undefined, true)
                .css({
                  color: tooltipCfg.color,
                  width: tooltipCfg.maxWidth,
                  opacity: 0,
                  zIndex: 11111,
                })
                .attr({
                  className: 'tooltip',
                  class: 'tooltip xLabel',
                  fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
                  stroke: tooltipCfg.borderColor,
                  zIndex: 11111,
                  height: 11,
                  'dominant-baseline': 'auto',
                  'stroke-width': tooltipCfg.borderWidth,
                  'stroke-dasharray':
                    tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
                  'stroke-opacity': tooltipCfg.opacity,
                  padding: 8,
                  r: Number(tooltipCfg.borderRadius.replace('px', '')),
                })
                .add()
                .toFront();
              const tooltipRect = chart.xAxisTooltip.getBBox();
              const tooltipBorderWidth = Number(tooltipCfg.borderWidth.replace('px', ''));
              let tooltipX =
                objRect && objRect !== null && chartRect && chartRect !== null
                  ? objRect!.x + objRect!.width / 2 - chartRect!.x - tooltipRect.width / 2
                  : 0;
              if (tooltipX < tooltipBorderWidth) tooltipX = tooltipBorderWidth;
              if (tooltipX + tooltipRect.width > chartRect!.width)
                tooltipX = chartRect!.width - tooltipRect.width - tooltipBorderWidth;
              chart.xAxisTooltip
                .attr({
                  x: 120,
                  y:
                    objRect && objRect !== null && chartRect && chartRect !== null
                      ? objRect!.y - chartRect!.y - tooltipRect.height + 25
                      : 0,
                  zIndex: 11111,
                })
                .css({
                  opacity: 1,
                  zIndex: 11111,
                });
              const htmlObj = document.querySelector<HTMLElement>(`[data-id='${value}']`);
              if (htmlObj) htmlObj.style.textShadow = textBoldShadow;
            },
            mouseout: function () {
              const obj = this as unknown as any;
              if (labelClicked) return setLabelClicked(false);
              const chart = chartRef.current;
              if (chart.xAxisTooltip) {
                chart.xAxisTooltip.destroy();
              }
              const value = obj.value.split("'")[3];
              const htmlObj = document.querySelector<HTMLElement>(`[data-id='${value}']`);
              if (htmlObj) htmlObj.style.textShadow = 'none';
            },
          },
          useHTML: true,
          style: {
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          },
        },
      },
      yAxis: {
        endOnTick: false,
        gridZIndex: -1,
        zIndex: -1,
        title: {
          text: chartYtitle,
          enabled: true,
        },
        stackLabels: {
          enabled: false,
        },
      },
      legend: {
        reversed: true,
        align: 'center',
        verticalAlign: 'top',
        floating: false,
        backgroundColor: HighchartsReact.defaultProps?.defaultOptions?.legend.backgroundColor || 'white',
        shadow: false,
      },
      tooltip: {
        ...highchartsTooltipCfg(tooltipCfg),
        useHTML: true,
        shape: 'square',
        formatter: function (): string {
          const obj = this as unknown as any;
          return `<div data-id='${`${prism.replace(' ', '')}${gridIndex}`}' id='${obj.key}}' data-name='${
            obj.key
          }' style='text-shadow:none;font-size:16px;margin-bottom:-12px;' class="clickableLabel">${
            obj.key
          }</div><br /><div data-id='${`${prism.replace(' ', '')}${gridIndex}`}' id='${Humanize.toFixed(
            obj.point.weight,
            2,
          )}' data-name='${Humanize.toFixed(
            obj.point.weight,
            2,
          )}' style='text-shadow:none' class="clickableLabel"><div style='display:inline-block;padding-top:6px;'><div style='width:10px;height:10px;display:inline-block;border-radius:50%;background:${
            obj.color
          }' /></div><div style='display:inline-block;padding-left:6px;'>${Humanize.toFixed(
            obj.point.weight,
            2,
          )}</div></div>`;
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        shared: false,
        style: {
          padding: '10px',
          width: '400px',
          fontSize: '14px',
          fontWeight: 'bold',
          fontStyle: 'normal',
          color: '#000000',
          zIndex: '19999',
        },
      },
      plotOptions: {
        series: {
          maxPointWidth: 30,
          cursor: 'pointer',
        },
        column: {
          borderWidth: 0,
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          },
        },
      },
      series: {
        type: 'wordcloud',
        data: seriesData,
      },
    };
  }, [
    data,
    size,
    exportFileName,
    onClick,
    chartRef,
    labelClicked,
    colorScheme,
    tooltipCfg,
    chartTitle,
    chartSubtitle,
    chartYtitle,
    gridIndex,
    prism,
    gridPrism,
    chartYmax,
    chartYmin,
  ]);

  return (
    <Root $gridheight={gridHeight}>
      <HighchartContainer>
        <HighchartsReact
          containerProps={{ style: { height: '100%' } }}
          style={{ flex: 1 }}
          highcharts={Highcharts}
          constructorType={'chart'}
          options={chartOptions ?? EMPTY_OPTIONS}
          callback={(chart: any) => (chartRef.current = chart)}
        />
      </HighchartContainer>
    </Root>
  );
}
